import { useEffect, useMemo, useState } from "react"
import { Box, Button, Typography } from "@mui/material"
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined"
import { observer } from "mobx-react-lite"
import { rootStore } from "stores/root-store"
import { ContentWrapper, MainComponent } from "components/common-styles"
import { TopbarPortal, TOPBAR_TITLE_SLOT_ID } from "components/layout/topbar-portal"
import { DEFAULT_PERIOD, deriveVolumeView } from "pages/analytics/volume/volume-derive"
import { PeriodFilter, downloadCsv } from "components/analytics/page-shared"
import { analyticsBusiness } from "businesses/home-business"
import { ChannelCard, CreatedDespatchedChart, OutboundMixChart, OutboundTable } from "components/analytics/widgets"
import { trackEvent } from "utils/analytics"

const C = { text: "#12131A", muted: "#65748B", brand: "#F3DA00", surfaceMuted: "#F9FAFC" }

const gridSx = { display: "grid", gap: 1.5 }
const EMPTY_VOLUME_PAYLOAD = { info: {}, outbound: [], inbound: [], dwh_last_date: null, dwh_unavailable: true }

const OutboundAnalytics = observer(() => {
  const [period, setPeriod] = useState(DEFAULT_PERIOD)
  const [payload, setPayload] = useState(EMPTY_VOLUME_PAYLOAD)

  useEffect(() => {
    rootStore.setScreenTitle("")
  }, [])

  const company = rootStore.company

  useEffect(() => {
    let cancelled = false

    const loadAnalytics = async () => {
      const next = await analyticsBusiness.getOutboundVolumePayload(company)
      if (!cancelled) setPayload(next)
    }

    loadAnalytics()
    return () => {
      cancelled = true
    }
  }, [company])

  useEffect(() => {
    trackEvent("analytics_view", { report: "outbound", period: typeof period === "string" ? period : "custom" })
  }, [period, company])

  const view = useMemo(() => deriveVolumeView(payload, period), [payload, period])
  const minSelectableYear = useMemo(() => {
    const years = (payload?.outbound || []).map(row => Number(String(row?.year_month || "").slice(0, 4))).filter(Number.isFinite)
    return years.length ? Math.min(...years) : null
  }, [payload])
  const ob = view.outboundKpis

  const exportOutbound = () =>
    downloadCsv(
      `outbound-${view.companyCode || "customer"}.csv`,
      [
        { key: "label", label: "Month" },
        { key: "created", label: "Orders created" },
        { key: "despatched", label: "Orders despatched" },
        { key: "uo", label: "Units/order" },
        { key: "yoy", label: "YoY %" },
      ],
      view.outboundDetail,
      "analytics_outbound",
    )

  return (
    <MainComponent component="main" sx={{ bgcolor: C.surfaceMuted, minHeight: "100%", display: "flex" }}>
      <ContentWrapper maxWidth={false} sx={{ py: 2 }}>
        <TopbarPortal slot={TOPBAR_TITLE_SLOT_ID}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 800, color: C.text, letterSpacing: -0.3, whiteSpace: "nowrap" }}>Outbound analysis</Typography>
            <Typography variant="caption" sx={{ color: C.muted, whiteSpace: "nowrap", display: { xs: "none", xl: "block" } }}>
              {view.period.label} · {view.name}
            </Typography>
          </Box>
        </TopbarPortal>

        <TopbarPortal>
          <PeriodFilter period={period} onChange={setPeriod} minYear={minSelectableYear} />
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={exportOutbound}
            sx={{ bgcolor: C.brand, color: C.text, fontWeight: 700, "&:hover": { bgcolor: "#dcc500" } }}
          >
            Outbound CSV
          </Button>
        </TopbarPortal>

        {/* Per-channel KPI cards — B2C / B2B, each with Created | Despatched inside */}
        <Box sx={{ ...gridSx, gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, mb: 1.5 }}>
          <ChannelCard channel="B2C" dot={C.brand} data={ob.b2c} />
          <ChannelCard channel="B2B" dot={C.text} data={ob.b2b} />
        </Box>

        {/* Created vs Despatched — Orders + Units */}
        <Box sx={{ ...gridSx, gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, mb: 1.5 }}>
          <CreatedDespatchedChart
            title="Created vs Despatched by Month — Orders (B2C+B2B)"
            subtitle="All received orders · by creation / despatch date"
            labels={view.labels}
            partialIndex={view.partialIndex}
            rows={view.ordersChart}
          />
          <CreatedDespatchedChart
            title="Created vs Despatched by Month — Units (B2C+B2B)"
            subtitle="Units / pieces across all orders · by creation / despatch date"
            labels={view.labels}
            partialIndex={view.partialIndex}
            rows={view.unitsChart}
          />
        </Box>

        {/* Outbound mix (Orders|Units toggle) + detail */}
        <Box sx={{ ...gridSx, gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" } }}>
          <OutboundMixChart labels={view.labels} partialIndex={view.partialIndex} mixChart={view.mixChart} />
          <OutboundTable data={view} />
        </Box>
      </ContentWrapper>
    </MainComponent>
  )
})

export default OutboundAnalytics
